import { html, TemplateResult, render } from 'lit';
import { renderKeptText } from '../../language/kept-text';
import { KeptText } from '../../language/kept-text-value';

/**
 * What a toast's title or message may be: a plain string, shown exactly as it stands (resolved
 * words, or text a person typed); the server's text still tagged (KeptText), resolved here once so a
 * person tag shows as that person's current name (@VOC-SERVER-002); or the caller's own template.
 */
export type ToastText = string | KeptText | TemplateResult;

/** A toast's title or message as it renders: kept text resolved, anything else as it stands. */
function renderToastText(text: ToastText): string | TemplateResult {
  return text instanceof KeptText ? renderKeptText(text.text) : text;
}

/**
 * Helper to show a Shoelace toast notification.
 * @param type Shoelace toast type: 'success', 'warning', 'error', 'info'
 * @param title Title of the toast (ToastText)
 * @param message Message content (ToastText)
 * @param duration Duration in ms (default 4000)
 */
let _headerObserverAttached = false;
let _cachedHeaderOffset = 0;
function ensureToastContainerPosition(container: HTMLElement) {
  // Attempt to locate header
  const header =
    document.getElementById('appHeaderRoot') ||
    document.querySelector('app-header');
  if (header instanceof HTMLElement) {
    const computeAndApply = () => {
      const rect = header.getBoundingClientRect();
      _cachedHeaderOffset = rect.bottom;
      container.style.top = `${_cachedHeaderOffset + 12}px`; // 12px spacing below header
    };
    computeAndApply();
    if (!_headerObserverAttached) {
      const ro = new ResizeObserver(() => computeAndApply());
      ro.observe(header);
      window.addEventListener('scroll', computeAndApply, { passive: true });
      window.addEventListener('orientationchange', computeAndApply);
      _headerObserverAttached = true;
    }
  } else {
    // Fallback fixed offset if header not yet rendered
    container.style.top = '4.5rem';
  }
}

/**
 * Shows a toast. A KeptText title or message is the server's text still tagged, rendered through
 * renderKeptText: term tags resolved and a person tag shown as that person's current name with their
 * card (D-162, @VOC-SERVER-002). A plain string is never read for tags, so typed text that looks like
 * one is shown as typed (398-f1 review 1, F1). A TemplateResult is the caller's own rendering and
 * passes through untouched.
 */
export function showToast(
  type: 'success' | 'warning' | 'error' | 'info',
  title: ToastText,
  message: ToastText,
  duration = 4000
) {
  // Create toast container if not present
  let container = document.getElementById('easy-toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'easy-toast-container';
    container.style.position = 'fixed';
    // top will be dynamically positioned below header
    container.style.right = '1.5rem';
    container.style.zIndex = '9999';
    document.body.appendChild(container);
    ensureToastContainerPosition(container);
  } else {
    ensureToastContainerPosition(container);
  }

  // Create a wrapper for this toast
  const toastWrapper = document.createElement('div');
  container.appendChild(toastWrapper);

  // Shoelace icon name by type
  const iconMap = {
    success: 'check2-circle',
    warning: 'exclamation-triangle',
    error: 'x-circle',
    info: 'info-circle',
  };
  // Shoelace alert variant by type
  const variantMap = {
    success: 'success',
    warning: 'warning',
    error: 'danger',
    info: 'primary',
  } as const;

  // Render the toast
  render(
    html`
      <sl-alert
        variant="${variantMap[type]}"
        open
        style="min-width: 320px; margin-bottom: 0.5rem;"
        @sl-after-hide=${() => {
          toastWrapper.remove();
        }}
      >
        <sl-icon slot="icon" name="${iconMap[type]}"></sl-icon>
        <strong>${renderToastText(title)}</strong>
        <div style="margin-top: 0.25rem;">
          ${
            message instanceof KeptText || typeof message === 'string'
              ? html`<span>${renderToastText(message)}</span>`
              : message
          }
        </div>
      </sl-alert>
    `,
    toastWrapper
  );

  // Auto-dismiss after duration
  setTimeout(() => {
    const alert = toastWrapper.querySelector('sl-alert') as HTMLElement & {
      hide?: () => void;
    };
    if (alert && typeof alert.hide === 'function') alert.hide();
  }, duration);
}

export function showSuccessToast(
  title: ToastText,
  message: ToastText,
  duration = 4000
) {
  showToast('success', title, message, duration);
}
export function showWarningToast(
  title: ToastText,
  message: ToastText,
  duration = 4000
) {
  showToast('warning', title, message, duration);
}
export function showErrorToast(
  title: ToastText,
  message: ToastText,
  duration = 4000
) {
  showToast('error', title, message, duration);
}
export function showInfoToast(
  title: ToastText,
  message: ToastText,
  duration = 4000
) {
  showToast('info', title, message, duration);
}
